import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box } from "@mui/material";
import "../BrandMetadataReview.css";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";

import { GridColDef } from "@mui/x-data-grid-premium";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import { renderNumberCell } from "utils/dataGrid";

interface BrandMetadataReviewProps {
  chain_id: string;
  country_code: string;
}

const columns_def: GridColDef[] = [
  {
    field: "chain_id",
    headerName: "Chain ID / Brand ID",
    type: "string",
    width: 200,
  },
  {
    field: "brand_id",
    headerName: "Brand ID",
    type: "string",
    width: 100,
  },
  {
    field: "metadata_website",
    headerName: "Metadata Website",
    description: "Website from a list of all websites in brand metadata",
    type: "string",
    width: 200,
  },
  {
    field: "actual_website",
    headerName: "Actual Website",
    description: "A website associated with the brand in the places table.",
    type: "string",
    width: 200,
  },
  {
    field: "actual_brand_website_count",
    headerName: "Place Website Count",
    description:
      "The count of places associated with this brand website in this country.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
];

const ReviewBrandWebsitesByCountry: React.FC<BrandMetadataReviewProps> = ({
  chain_id,
  country_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();
  useEffect(() => {
    if (chain_id && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        chain_id: chain_id,
        country_code: country_code,
      });

      const fetchWebsites = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/websites_by_country?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch websites to review for ${chain_id} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchWebsites();
    } else {
      setData([]);
    }
  }, [chain_id, country_code]);

  const user_instructions = {
    section_id: "brand-websites",
    instruction_title: "Review Websites",
    purpose:
      "Accurate metadata about the websites associated with a brand is crucial for ensuring high-quality results in our chain identification process. This process associates places in our database with specific brands by assigning a unique `chain_id` value to each brand. The `chain_id` remains consistent across all places associated with the brand, ensuring accurate grouping and identification.\n\nIn addition to enhancing the accuracy of the chain identification process, maintaining a comprehensive list of valid websites serves as a reference during the places review process. Typically, invalid websites are linked to invalid places that should not be associated with the brand.",
    goal: "Ensure all valid websites for the brand, associated with the selected country of operation, are accurately reflected in the metadata.",
    task_description:
      "The table below compares the websites listed in the `websites` attribute in the metadata with the actual website values linked to places in the dataplor database for the brand.",
    task_steps: [
      {
        title: "Identify Missing Websites",
        instructions: [
          "Review the table below to locate any websites associated with places in the database that are missing from the metadata.",
        ],
      },
      {
        title: "Validate Missing Websites",
        instructions: [
          "Research and verify whether the missing websites are valid and applicable to the brand for the selected country of operation.",
        ],
      },
      {
        title: "Update Metadata",
        instructions: [
          {
            text: "For validated websites, update the `websites` attribute in `Kuebiko`:",
            sublist: [
              "Navigate to the brand in `Kuebiko`.",
              "Select `Brand Edit`.",
              "Update the metadata to include the missing websites.",
            ],
          },
        ],
      },
    ],
  };

  return (
    <Box className="metadata-review-container">
      <BrandReviewInstructions {...user_instructions} />
      <GaneshDataGrid
        gridId="reviewBrandWebsitesByCountry"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewBrandWebsitesByCountry;
